import React, { useState, useEffect } from 'react';
import {
  getBrandFromSku,
  getSkuCategory,
  getSkuCategoryName,
  getTemplatesForSku,
  getFilsonTemplateByOrientation
} from '../utils/templateMapping';

const ProductTable = ({
  products,
  productStates: externalProductStates = null,
  onProductsChange,
  allPrinters = [],
  tranid,
  transactionType, // 'TO' or 'SO'
  onPrintLine,
  defaultPriceSettings = { selectedCurrency: 'USD', selectedPriceType: 'current' }
}) => {
  // Initialize product states with quantity, selected template, orientation, and pricing
  const [productStates, setProductStates] = useState({});

  // Initialize states when products change
  useEffect(() => {
    if (products && products.length > 0) {
      const initialStates = {};
      products.forEach((product, index) => {
        const brand = getBrandFromSku(product.itemid);
        const isNoPrice = defaultPriceSettings.selectedPriceType === 'none';
        const templates = getTemplatesForSku(product.itemid, isNoPrice);

        // Get default template
        let defaultTemplate = '';
        if (brand === 'Shinola' && templates.length > 0) {
          // Select first non-manufacturing template
          const nonManufacturingTemplates = templates.filter(t => !t.manufacturing);
          defaultTemplate = nonManufacturingTemplates.length > 0
            ? nonManufacturingTemplates[0].path
            : templates[0].path;
        } else if (brand === 'Filson') {
          defaultTemplate = getFilsonTemplateByOrientation('90');
        }

        initialStates[index] = {
          quantity: Math.abs(product.quantity || 1),
          selectedTemplate: defaultTemplate,
          orientation: '90',
          selectedPrinter: '',
          expanded: false,
          enabled: true, // Whether this line is active for printing
          // Pricing options - use defaults from parent
          selectedCurrency: defaultPriceSettings.selectedCurrency || 'USD', // 'USD' or 'CAD'
          selectedPriceType: defaultPriceSettings.selectedPriceType || 'current' // 'current', 'future', 'line', or 'none'
        };
      });
      setProductStates(initialStates);

      // Notify parent of initial states
      onProductsChange(initialStates);
    }
  }, [products]);

  // Sync with external state changes (when parent updates productStates)
  useEffect(() => {
    if (externalProductStates && Object.keys(externalProductStates).length > 0) {
      setProductStates(externalProductStates);
    }
  }, [externalProductStates]);

  const updateProductState = (index, updates) => {
    const newStates = {
      ...productStates,
      [index]: {
        ...productStates[index],
        ...updates
      }
    };
    setProductStates(newStates);
    onProductsChange(newStates);
  };

  const handleQuantityChange = (index, value) => {
    // Allow empty string for easier editing
    if (value === '') {
      updateProductState(index, { quantity: '' });
      return;
    }

    // Only accept numeric values
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      updateProductState(index, { quantity: numValue });
    }
  };

  const handleQuantityBlur = (index) => {
    const state = productStates[index];
    // If empty or 0, set to 1 as minimum
    if (!state.quantity || state.quantity === '' || state.quantity === 0) {
      updateProductState(index, { quantity: 1 });
    }
  };

  const handleTemplateChange = (index, template) => {
    updateProductState(index, { selectedTemplate: template });
  };

  const handleOrientationChange = (index, orientation) => {
    const template = getFilsonTemplateByOrientation(orientation);
    updateProductState(index, { orientation, selectedTemplate: template });
  };

  const handlePrinterChange = (index, printerId) => {
    updateProductState(index, { selectedPrinter: printerId });
  };

  const handleCurrencyChange = (index, currency) => {
    updateProductState(index, { selectedCurrency: currency });
  };

  const handlePriceTypeChange = (index, priceType) => {
    updateProductState(index, { selectedPriceType: priceType });
  };

  const toggleEnabled = (index, event) => {
    event.stopPropagation();
    updateProductState(index, { enabled: !productStates[index]?.enabled });
  };

  const toggleExpand = (index) => {
    updateProductState(index, { expanded: !productStates[index]?.expanded });
  };

  // Helper function to get the selected price for a product
  const getSelectedPrice = (product, state) => {
    if (!product || !state) return null;

    const { selectedCurrency, selectedPriceType } = state;

    // If no price is selected, return empty string
    if (selectedPriceType === 'none') {
      return '';
    }

    // If line price is selected, use transaction line price
    if (selectedPriceType === 'line') {
      return product.transaction_line_price || product.usd_current_price || '0.00';
    }

    // If outlet price is selected, use outlet prices with currency selection
    if (selectedPriceType === 'outlet') {
      if (selectedCurrency === 'USD') {
        return product.usd_outlet_price || '0.00';
      } else { // CAD
        return product.cad_outlet_price || '0.00';
      }
    }

    // Otherwise, use selected currency and price type
    if (selectedCurrency === 'USD') {
      return selectedPriceType === 'future'
        ? (product.usd_future_price || product.usd_current_price || '0.00')
        : (product.usd_current_price || '0.00');
    } else { // CAD
      return selectedPriceType === 'future'
        ? (product.cad_future_price || product.cad_current_price || '0.00')
        : (product.cad_current_price || '0.00');
    }
  };

  if (!products || products.length === 0) {
    return <div>No products to display</div>;
  }

  // Group printers like global PrinterSelect (by FQDName with sections)
  const groupedPrinters = (allPrinters || []).reduce((groups, printer) => {
    const fqdName = printer.Client?.FQDName || 'Unknown';
    if (!groups[fqdName]) groups[fqdName] = [];
    groups[fqdName].push(printer);
    return groups;
  }, {});
  const sortedGroupKeys = Object.keys(groupedPrinters).sort();
  sortedGroupKeys.forEach(key => {
    groupedPrinters[key].sort((a, b) => a.Name.localeCompare(b.Name));
  });

  return (
    <div className="product-table-container">
      <style jsx>{`
        .product-table-container {
          width: 100%;
        }

        .products-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 15px 20px;
          border-radius: 8px 8px 0 0;
          font-weight: 600;
          font-size: 1.1rem;
          display: flex;
          justify-content: flex-start;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
        }

        .product-card {
          background: white;
          border: 1px solid #e1e5e9;
          border-top: none;
          transition: all 0.3s ease;
        }

        .product-card.disabled {
          background: #f5f5f5;
          opacity: 0.6;
        }

        .product-card:last-child {
          border-radius: 0 0 8px 8px;
        }

        .product-summary {
          display: grid;
          grid-template-columns: 50px 1.5fr 3fr 1fr 1.5fr 80px;
          gap: 15px;
          padding: 20px;
          cursor: pointer;
          transition: background 0.2s;
          align-items: center;
        }

        .product-summary:hover {
          background: #f8f9fa;
        }

        .hide-on-mobile {
          display: block;
        }

        .product-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .field-label {
          font-size: 0.75rem;
          color: #666;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .field-value {
          font-size: 1rem;
          color: #2c3e50;
          font-weight: 500;
        }

        .quantity-input {
          width: 100%;
          max-width: 100px;
          padding: 8px 12px;
          border: 2px solid #dee2e6;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 600;
          text-align: center;
          transition: all 0.2s;
        }

        .quantity-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .quantity-input:disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }

        .quantity-input:not(:disabled):hover {
          border-color: #667eea;
        }

        .expand-icon {
          font-size: 1.2rem;
          transition: transform 0.3s;
          color: #667eea;
        }

        .expand-icon.expanded {
          transform: rotate(180deg);
        }

        .product-details {
          padding: 0 20px 20px 20px;
          border-top: 1px solid #e1e5e9;
          background: #f8fafc;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .detail-item {
          background: white;
          padding: 12px;
          border-radius: 6px;
          border-left: 3px solid #667eea;
        }

        .detail-label {
          font-size: 0.8rem;
          color: #666;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .detail-value {
          font-size: 0.95rem;
          color: #2c3e50;
        }

        .brand-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .brand-shinola {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: white;
        }

        .brand-filson {
          background: linear-gradient(135deg, #2c3e50, #34495e);
          color: white;
        }

        .template-section {
          margin-top: 15px;
          padding: 15px;
          background: white;
          border-radius: 8px;
        }

        .template-section h5 {
          margin: 0 0 12px 0;
          color: #2c3e50;
          font-size: 1rem;
          font-weight: 600;
        }

        .template-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .template-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f8f9fa;
          border: 2px solid #dee2e6;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .template-option:hover {
          border-color: #667eea;
          background: #e3f2fd;
        }

        .template-option.selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: #667eea;
        }

        .template-option input[type="radio"] {
          accent-color: #667eea;
        }

        .template-name {
          font-weight: 500;
          flex: 1;
        }

        .orientation-section {
          display: flex;
          gap: 10px;
          margin-top: 12px;
        }

        .orientation-btn {
          flex: 1;
          padding: 10px;
          background: #f8f9fa;
          border: 2px solid #dee2e6;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .orientation-btn:hover {
          border-color: #667eea;
          background: #e3f2fd;
        }

        .orientation-btn.selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: #667eea;
        }

        .no-template-warning {
          padding: 12px;
          background: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 6px;
          color: #856404;
        }

        .printer-select {
          margin-top: 12px;
        }

        .line-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 12px;
        }

        .toggle-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 2px solid #dee2e6;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .toggle-btn.enabled {
          background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
          border-color: #4caf50;
          color: white;
          box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
        }

        .toggle-btn.disabled {
          background: #f5f5f5;
          border-color: #ccc;
          color: #999;
        }

        .toggle-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .toggle-btn:active {
          transform: scale(0.95);
        }

        /* Tablet styles */
        @media (max-width: 1024px) {
          .product-summary {
            grid-template-columns: 50px 2fr 1fr 2fr 1fr;
            gap: 12px;
          }

          .hide-on-mobile {
            display: none;
          }
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .product-summary {
            grid-template-columns: 50px 1fr;
            gap: 12px;
          }

          .hide-on-mobile {
            display: none;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .orientation-section {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="products-header">

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>📦 Items from {tranid}</span>
          <span style={{
            background: 'rgba(255, 255, 255, 0.2)',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '0.85rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>💰</span>
            <span>
              {defaultPriceSettings.selectedPriceType === 'none'
                ? 'No Price'
                : defaultPriceSettings.selectedPriceType === 'line'
                  ? 'Line Price'
                  : `${defaultPriceSettings.selectedCurrency} ${defaultPriceSettings.selectedPriceType === 'current' ? 'Current' : 'Future'}`
              }
            </span>
          </span>
          <p style={{ marginBottom: '15px', fontSize: '0.95rem' }}>
            💡 Expand each item to select item-specific options, such as printer, price type, etc.
          </p>

        </div>
        <span>
          {Object.values(productStates).filter(s => s.enabled).length} / {products.length} active
        </span>
      </div>

      {products.map((product, index) => {
        const brand = getBrandFromSku(product.itemid);
        const category = getSkuCategory(product.itemid);
        const categoryName = getSkuCategoryName(category);
        const state = productStates[index] || {};
        const isNoPrice = state.selectedPriceType === 'none';
        const templates = getTemplatesForSku(product.itemid, isNoPrice);
        const isExpanded = state.expanded;

        return (
          <div key={index} className={`product-card ${!state.enabled ? 'disabled' : ''}`}>
            <div
              className="product-summary"
              onClick={() => toggleExpand(index)}
            >
              {/* Toggle Button */}
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className={`toggle-btn ${state.enabled ? 'enabled' : 'disabled'}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleEnabled(index, e);
                  }}
                  title={state.enabled ? 'Click to disable this line' : 'Click to enable this line'}
                >
                  {state.enabled ? '✓' : '✕'}
                </button>
              </div>

              <div className="product-field">
                <div className="field-label">SKU</div>
                <div className="field-value">{product.itemid || 'N/A'}</div>
              </div>

              <div className="product-field hide-on-mobile">
                <div className="field-label">Display Name</div>
                <div className="field-value" style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {product.displayname || product.stockdescription || 'N/A'}
                </div>
              </div>

              <div className="product-field">
                <div className="field-label">Quantity</div>
                <input
                  type="number"
                  className="quantity-input"
                  value={state.enabled ? (state.quantity === '' ? '' : state.quantity || 1) : 0}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  onBlur={() => handleQuantityBlur(index)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={!state.enabled}
                  style={{
                    opacity: state.enabled ? 1 : 0.5,
                    cursor: state.enabled ? 'text' : 'not-allowed'
                  }}
                />
              </div>

              <div className="product-field">
                <div className="field-label">Price</div>
                <div className="field-value">
                  {state.selectedPriceType === 'none' ? (
                    <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>No price</span>
                  ) : (
                    <>
                      ${getSelectedPrice(product, state)}
                      {state.selectedCurrency === 'CAD' && state.selectedPriceType !== 'line' && (
                        <span style={{ fontSize: '0.8em', marginLeft: '4px', color: '#667eea' }}>CAD</span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
            </div>

            {isExpanded && (
              <div className="product-details">
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-label">Display Name</div>
                    <div className="detail-value">{product.displayname || 'N/A'}</div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">UPC</div>
                    <div className="detail-value">{product.upccode || 'N/A'}</div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">Brand</div>
                    <div className="detail-value">
                      {brand && (
                        <span className={`brand-badge brand-${brand.toLowerCase()}`}>
                          {brand}
                        </span>
                      )}
                      {!brand && 'Unknown'}
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">Category</div>
                    <div className="detail-value">{categoryName || 'N/A'}</div>
                  </div>

                  {product.product_color && (
                    <div className="detail-item">
                      <div className="detail-label">Color</div>
                      <div className="detail-value">{product.product_color}</div>
                    </div>
                  )}

                  {product.product_size && (
                    <div className="detail-item">
                      <div className="detail-label">Size</div>
                      <div className="detail-value">{product.product_size}</div>
                    </div>
                  )}
                </div>

                {/* Pricing Configuration (for all transaction types) */}
                <div className="template-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <h5 style={{ margin: 0 }}>💰 Pricing Configuration</h5>
                    {state.selectedCurrency === defaultPriceSettings.selectedCurrency &&
                      state.selectedPriceType === defaultPriceSettings.selectedPriceType && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#667eea',
                          background: 'rgba(102, 126, 234, 0.1)',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontWeight: '600'
                        }}>
                          ✓ Using Default
                        </span>
                      )}
                    {(state.selectedCurrency !== defaultPriceSettings.selectedCurrency ||
                      state.selectedPriceType !== defaultPriceSettings.selectedPriceType) && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#f59e0b',
                          background: 'rgba(245, 158, 11, 0.1)',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontWeight: '600'
                        }}>
                          ⚡ Custom
                        </span>
                      )}
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
                      Select currency and price type for the label:
                    </div>

                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      {/* Currency Selection */}
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div className="field-label" style={{ marginBottom: '8px' }}>
                          Region / Currency
                          {(state.selectedPriceType === 'line' || state.selectedPriceType === 'none') && (
                            <span style={{ fontSize: '0.75rem', color: '#999', fontWeight: 'normal', marginLeft: '8px' }}>
                              (N/A for {state.selectedPriceType === 'line' ? 'line price' : 'no price'})
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedCurrency === 'USD' ? 'selected' : ''}`}
                            onClick={() => handleCurrencyChange(index, 'USD')}
                            disabled={state.selectedPriceType === 'line' || state.selectedPriceType === 'none'}
                            style={{
                              flex: '1',
                              minWidth: '80px',
                              opacity: (state.selectedPriceType === 'line' || state.selectedPriceType === 'none') ? 0.5 : 1,
                              cursor: (state.selectedPriceType === 'line' || state.selectedPriceType === 'none') ? 'not-allowed' : 'pointer'
                            }}
                          >
                            🇺🇸 USD
                          </button>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedCurrency === 'CAD' ? 'selected' : ''}`}
                            onClick={() => handleCurrencyChange(index, 'CAD')}
                            disabled={state.selectedPriceType === 'line' || state.selectedPriceType === 'none'}
                            style={{
                              flex: '1',
                              minWidth: '80px',
                              opacity: (state.selectedPriceType === 'line' || state.selectedPriceType === 'none') ? 0.5 : 1,
                              cursor: (state.selectedPriceType === 'line' || state.selectedPriceType === 'none') ? 'not-allowed' : 'pointer'
                            }}
                          >
                            🇨🇦 CAD
                          </button>
                        </div>
                      </div>

                      {/* Price Type Selection */}
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div className="field-label" style={{ marginBottom: '8px' }}>Price Type</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedPriceType === 'current' ? 'selected' : ''}`}
                            onClick={() => handlePriceTypeChange(index, 'current')}
                            style={{ flex: '1', minWidth: '100px' }}
                          >
                            Current Retail
                          </button>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedPriceType === 'future' ? 'selected' : ''}`}
                            onClick={() => handlePriceTypeChange(index, 'future')}
                            style={{ flex: '1', minWidth: '100px' }}
                          >
                            Future Retail
                          </button>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedPriceType === 'outlet' ? 'selected' : ''}`}
                            onClick={() => handlePriceTypeChange(index, 'outlet')}
                            style={{ flex: '1', minWidth: '100px' }}
                          >
                            Outlet
                          </button>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedPriceType === 'line' ? 'selected' : ''}`}
                            onClick={() => handlePriceTypeChange(index, 'line')}
                            style={{ flex: '1', minWidth: '100px' }}
                          >
                            📋 Line Price
                          </button>
                          <button
                            type="button"
                            className={`orientation-btn ${state.selectedPriceType === 'none' ? 'selected' : ''}`}
                            onClick={() => handlePriceTypeChange(index, 'none')}
                            style={{ flex: '1', minWidth: '100px' }}
                          >
                            🚫 No Price
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Price Display */}
                    <div style={{
                      marginTop: '12px',
                      padding: '15px',
                      background: state.selectedPriceType === 'none'
                        ? 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
                        : state.selectedPriceType === 'line'
                          ? 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)'
                          : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                      borderRadius: '8px',
                      borderLeft: state.selectedPriceType === 'none'
                        ? '4px solid #9e9e9e'
                        : state.selectedPriceType === 'line' ? '4px solid #4caf50' : '4px solid #667eea',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                            {state.selectedPriceType === 'none'
                              ? 'No Price'
                              : state.selectedPriceType === 'line'
                                ? `${transactionType} Transaction Line Price`
                                : state.selectedPriceType === 'current'
                                  ? 'Current Retail Price'
                                  : state.selectedPriceType === 'outlet'
                                    ? 'Outlet Price'
                                    : 'Future Retail Price'}
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: state.selectedPriceType === 'none' ? '#9e9e9e' : state.selectedPriceType === 'line' ? '#4caf50' : '#667eea' }}>
                            {state.selectedPriceType === 'none'
                              ? 'No price on label'
                              : `$${getSelectedPrice(product, state)} ${state.selectedPriceType !== 'line' ? state.selectedCurrency : ''}`}
                          </div>
                        </div>
                        {state.selectedPriceType !== 'line' && state.selectedPriceType !== 'none' && product.transaction_line_price && (
                          <div style={{
                            padding: '8px 12px',
                            background: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: '6px',
                            border: '1px solid rgba(76, 175, 80, 0.3)'
                          }}>
                            <div style={{ fontSize: '0.75rem', color: '#666' }}>{transactionType} Line Price</div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#4caf50' }}>
                              ${product.transaction_line_price}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Template Selection */}
                <div className="template-section">
                  <h5>Template Selection</h5>

                  {brand === 'Shinola' && templates.length > 0 && (
                    <div className="template-list">
                      {templates.filter(t => !t.manufacturing).map((template, tIdx) => (
                        <label
                          key={tIdx}
                          className={`template-option ${state.selectedTemplate === template.path ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name={`template-${index}`}
                            value={template.path}
                            checked={state.selectedTemplate === template.path}
                            onChange={(e) => handleTemplateChange(index, e.target.value)}
                          />
                          <span className="template-name">{template.name}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {brand === 'Filson' && (
                    <div>
                      <div className="orientation-section">
                        <button
                          type="button"
                          className={`orientation-btn ${state.orientation === '0' ? 'selected' : ''}`}
                          onClick={() => handleOrientationChange(index, '0')}
                        >
                          Legacy
                        </button>
                        <button
                          type="button"
                          className={`orientation-btn ${state.orientation === '90' ? 'selected' : ''}`}
                          onClick={() => handleOrientationChange(index, '90')}
                        >
                          Standard
                        </button>
                      </div>
                      <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
                        Selected: {state.selectedTemplate?.split('/').pop()}
                      </div>
                    </div>
                  )}

                  {!brand || (brand === 'Shinola' && templates.length === 0) && (
                    <div className="no-template-warning">
                      ⚠️ No templates available for this SKU. Please verify the product category.
                    </div>
                  )}

                  {/* Per-line Printer Selection */}
                  {allPrinters && allPrinters.length > 0 && (
                    <div className="printer-select">
                      <div className="field-label">Printer (optional per item)</div>
                      <select
                        className="form-control"
                        value={state.selectedPrinter || ''}
                        onChange={(e) => handlePrinterChange(index, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">-- Use Global Printer --</option>
                        {sortedGroupKeys.map((fqdName) => (
                          <optgroup key={fqdName} label={fqdName}>
                            {groupedPrinters[fqdName].map((printer) => (
                              <option key={printer.PrinterId} value={printer.PrinterId}>
                                {printer.Name} ({printer.Client.FQDName})
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Per-line print action */}
                  {typeof onPrintLine === 'function' && (
                    <div className="line-actions">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={(e) => { e.stopPropagation(); onPrintLine(index); }}
                      >
                        🖨️ Print This Item
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProductTable;

